/** @format */
import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import ReactDOM from "react-dom";
import DataTable from "react-data-table-component";
import { formatDate } from "../../../lib/FormatDate";
import { connect } from "react-redux";
import CustomPagination from "../inventory-pagination";
import ActivityModal from "../modals/activity-modal";
import DataTableFilter from "../table-filter";
import Slots from "../containers/credits-container";
import GenerateCollapsibleCategories from "../collapsible";
import NoData from "../../../images/table-images/empty-inventory.svg";
import { Link } from "react-router-dom";
import { fetchActivities } from "../../../redux/activities/FetchActivitiesActions";
import ReportInventoryModal from "../report-inventory";
import EmailOrganizationModal from "../modals/email-modal";
import DeleteInventory from "../modals/delete-inventory";
import { Tooltip } from "@jobtarget/ui-library";

/**
 *
 * @param {array} inventory
 * @param {function} updateKnownValues
 *
 * This component is responsible for initializing the activities Datatables.  This Datatable allows the user to add an activity in the "Actions" column. When adding an activity, the user will interface with the Add Activity Modal.
 *
 */

const ActionsDropdown = ({ row, onAction }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const btnRef = useRef(null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });

  const handleClickOutside = useCallback((e) => {
    if (ref.current && !ref.current.contains(e.target)) {
      setOpen(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [handleClickOutside]);

  const handleToggle = () => {
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setMenuPos({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
    setOpen((prev) => !prev);
  };

  const handleSelect = (action) => {
    setOpen(false);
    onAction(action);
  };

  return (
    <div ref={ref}>
      <button
        ref={btnRef}
        className="btn btn-sm btn-outline-secondary"
        onClick={handleToggle}
        aria-haspopup="true"
        aria-expanded={open}
        style={{ textTransform: "none", fontSize: "0.8rem", whiteSpace: "nowrap", padding: "6px 16px" }}
      >
        Select Action <i className="fal fa-chevron-down ml-2" style={{ fontSize: "0.65rem" }}></i>
      </button>
      {open && ReactDOM.createPortal(
        <div
          className="dropdown-menu shadow-sm show"
          style={{
            position: "absolute",
            top: menuPos.top,
            left: menuPos.left,
            zIndex: 1050,
            minWidth: menuPos.width || "auto",
            display: "block",
          }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <button className="dropdown-item d-flex align-items-center py-1" style={{ fontSize: "0.85rem" }} onClick={() => handleSelect("addActivity")}>
            <i className="far fa-pen-to-square text-primary mr-2"></i> Add Activity
          </button>
          <button className="dropdown-item d-flex align-items-center py-1" style={{ fontSize: "0.85rem" }} onClick={() => handleSelect("emailOrganization")}>
            <i className="far fa-envelope text-primary mr-2"></i> Email Org
          </button>
          <button className="dropdown-item d-flex align-items-center py-1" style={{ fontSize: "0.85rem" }} onClick={() => handleSelect("reportInventory")}>
            <i className="fal fa-triangle-exclamation text-primary mr-2"></i> Report an Issue
          </button>
          <button className="dropdown-item d-flex align-items-center py-1" style={{ fontSize: "0.85rem" }} onClick={() => handleSelect("deleteInventory")}>
            <i className="fas fa-trash-can text-danger mr-2"></i> Delete Org
          </button>
        </div>,
        document.body
      )}
    </div>
  );
};

const refreshSelectFields = function () {};

function InventoryTable({ inventory }) {
  const noDataImage = useMemo(() => {
    return (
      <div className="d-flex flex-column align-items-center pb-5">
        <img src={NoData} alt="" height="350px" />
        <h3>Start by Adding an Organization</h3>
        <p className="text-center">
          All organization records will be displayed here. Add an organization
          <br />
          by clicking the button below
        </p>
        <Link to="/search">
          <button
            // onClick={() => setShowCreateNewOrgModal(true)}

            className="btn btn-primary"
          >
            Add Organization
          </button>
        </Link>
      </div>
    );
  }, []);

  const [showAddActivityModal, setShowAddActivityModal] = useState(false);
  const [showReportInventoryModal, setShowReportInventoryModal] =
    useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [disabled, setDisabled] = useState(false);

  // State for character input upon filtering organizations in the user's inventory
  const [userInput, setUserInput] = useState("");

  // State which holds the necessary information for the row in focus while adding an activity.
  const [activeRowData, setActiveRowData] = useState({
    site_name: "",
    City: "",
    State: "",
    contact: "",
    ans: [],
  });

  const modalTogglerMenu = ({ row, modalToggler }) => {
    setActiveRowData(row);
    modalToggler(true);
  };

  const filteredInventory = useMemo(() => {
    // This function is used to filter the inventory based on the userInput state. It will return all objects which match the input characters
    try {
      return inventory.inventoryData.filter((item) => {
        let date = formatDate(item.createdAt);
        let formattedItem = { ...item, createdAt: date };
        return (
          (formattedItem.site_name &&
            formattedItem.site_name
              .toLowerCase()
              .includes(userInput.toLowerCase())) ||
          (formattedItem.ans.toString() &&
            formattedItem.ans
              .toString()
              .toLowerCase()
              .includes(userInput.toLowerCase())) ||
          (formattedItem.contact &&
            formattedItem.contact
              .toLowerCase()
              .includes(userInput.toLowerCase())) ||
          (formattedItem.rules &&
            formattedItem.rules
              .toLowerCase()
              .includes(userInput.toLowerCase())) ||
          (formatDate(formattedItem.added) &&
            formatDate(formattedItem.added)
              .toLowerCase()
              .includes(userInput.toLowerCase())) ||
          (formattedItem.City &&
            formattedItem.City.toLowerCase().includes(
              userInput.toLowerCase()
            )) ||
          (formattedItem.State &&
            formattedItem.State.toLowerCase().includes(userInput.toLowerCase()))
        );
      });
    } catch (error) {
      return "";
    }
  }, [inventory.inventoryData, userInput]);

  // this useEffect is to fix the bug where the dropdown in each row isn't fully visible when the user is down to 2 inventory
  useEffect(() => {
    if (inventory.inventoryData.length < 4) {
      const data = document.querySelector(".inventory-table");
      data.style.height = "300px";
    } else {
      const data = document.querySelector(".inventory-table");
      data.style.height = "auto";
    }
  }, [inventory.inventoryData]);

  useEffect(() => {
    if (filteredInventory.length < 2) {
      const data = document.querySelector(".inventory-table");
      data.style.height = "400px";
    }
    if (filteredInventory.length < 4) {
      const data = document.querySelector(".inventory-table");
      data.style.height = "300px";
    } else {
      const data = document.querySelector(".inventory-table");
      data.style.height = "auto";
    }
  }, [filteredInventory]);

  return (
    <>
      <DataTableFilter setUserInput={setUserInput} />
      <Slots />
      <DataTable
        className="inventory-table"
        disabled={disabled}
        columns={[
          {
            name: "Organization",
            selector: "site_name",
            sortable: true,
            wrap: true,
          },
          {
            name: "Contact",
            selector: (row) => {
              return (
                <button
                  className="btn btn-link p-0"
                  onClick={() =>
                    modalTogglerMenu({
                      row,
                      modalToggler: setShowEmailModal,
                    })
                  }
                >
                  {row.contact}
                </button>
              );
            },
            sortable: true,
            wrap: true,
          },
          {
            name: "Category",
            selector: (obj, i) => {
              if (obj.ans.length > 1) {
                const res = obj.ans.join(`\n`)
                return (
                  <div style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>
                    <Tooltip message={res} position="left">
                      <GenerateCollapsibleCategories obj={obj} i={i} />
                    </Tooltip>
                  </div>
                );
              } else return (
                <div style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>
                  <Tooltip message={obj.ans} position="left">
                    {obj.ans}
                  </Tooltip>
                </div>
              );
            },
            sortable: true,
            width: "10%",
          },
          {
            name: "Location",
            selector: (row) => `${row.City}, ${row.State}`,
            sortable: true,
          },

          {
            name: "Job Radius Rules",
            selector: (row) => row.rules,
            sortable: true,
          },

          {
            name: "Actions",
            allowOverflow: true,
            width: "210px",
            cell: (row) => {
              return (
                <ActionsDropdown
                  row={row}
                  onAction={(action) => {
                    if (action === "addActivity") {
                      modalTogglerMenu({
                        row,
                        modalToggler: setShowAddActivityModal,
                      });
                    } else if (action === "reportInventory") {
                      modalTogglerMenu({
                        row,
                        modalToggler: setShowReportInventoryModal,
                      });
                    } else if (action === "emailOrganization") {
                      modalTogglerMenu({
                        row,
                        modalToggler: setShowEmailModal,
                      });
                    } else if (action === "deleteInventory") {
                      modalTogglerMenu({
                        row,
                        modalToggler: setShowDeleteModal,
                      });
                    }
                  }}
                />
              );
            },
          },
        ]}
        data={filteredInventory}
        onChangePage={() => {
          // fix the dropdown menu bug
          refreshSelectFields();
        }}
        pagination
        paginationComponent={CustomPagination}
        highlightOnHover
        noHeader
        pointerOnHover
        progressPending={inventory.loading}
        onChangeRowsPerPage={(e) => {
          // fix the dropdown menu bug
          refreshSelectFields();
        }}
        noDataComponent={noDataImage}
      />
      <ActivityModal
        show={showAddActivityModal}
        onHide={() => setShowAddActivityModal(false)}
        activeRowData={activeRowData}
      />
      <ReportInventoryModal
        show={showReportInventoryModal}
        onHide={() => setShowReportInventoryModal(false)}
        row={activeRowData}
      />
      <EmailOrganizationModal
        show={showEmailModal}
        onHide={() => setShowEmailModal(false)}
        row={activeRowData}
      />
      <DeleteInventory
        showModal={showDeleteModal}
        setShowModal={setShowDeleteModal}
        row={activeRowData}
        setDisabled={setDisabled}
      />
    </>
  );
}

const mapStateToProps = (state) => {
  return {
    inventory: state.inventory,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    fetchActivities: (company_id) => dispatch(fetchActivities(company_id)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(InventoryTable);
